import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatError } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BellRing, CheckCircle2, Clock, XCircle } from "lucide-react";
import { formatTime } from "@/lib/time";

const POLL_MS = 30000;

export default function PendingBookingBanner() {
  const [intents, setIntents] = useState([]);

  const load = useCallback(() => {
    api.get("/booking-intents/pending").then(r => setIntents(r.data.intents || [])).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  const resolve = async (id, outcome) => {
    try {
      await api.post(`/booking-intents/${id}/resolve`, { outcome });
      if (outcome === "later") toast.success("Reminder snoozed for 10 more minutes");
      else if (outcome === "booked") toast.success("Marked as booked");
      else toast.success("Dismissed");
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  if (intents.length === 0) return null;
  return (
    <div className="space-y-2" data-testid="pending-booking-banner">
      {intents.map(intent => {
        const providerName = intent.provider === "opentable" ? "OpenTable" : intent.provider === "resy" ? "Resy" : "the booking site";
        const openedAgo = minutesAgo(intent.opened_at);
        return (
          <Card key={intent.id} className="p-4 border-amber-500 border-2 bg-amber-50/70 dark:bg-amber-900/10" data-testid="pending-intent-card">
            <div className="flex items-start gap-3 flex-wrap">
              <BellRing className="w-5 h-5 text-amber-600 mt-1 shrink-0"/>
              <div className="flex-1 min-w-0">
                <div className="text-sm">
                  Did you book <Link to={`/dinners/${intent.dinner_id}`} className="font-semibold text-primary underline">
                    {intent.restaurant_name || "the dinner"}
                  </Link> on {providerName}?
                </div>
                <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                  <Clock className="w-3 h-3"/>
                  Opened {openedAgo} min ago · dinner {intent.dinner?.dinner_date} at {formatTime(intent.dinner?.approved_time || intent.dinner?.recommended_time)}
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" asChild data-testid="intent-record-btn">
                  <Link to={`/dinners/${intent.dinner_id}`}><CheckCircle2 className="w-3 h-3 mr-1"/>Yes, record it</Link>
                </Button>
                <Button size="sm" variant="outline" onClick={()=>resolve(intent.id, "later")} data-testid="intent-later-btn">
                  Remind me in 10 min
                </Button>
                <Button size="sm" variant="ghost" onClick={()=>resolve(intent.id, "dismiss")} data-testid="intent-dismiss-btn">
                  <XCircle className="w-3 h-3"/>
                </Button>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

function minutesAgo(iso) {
  if (!iso) return "?";
  const diff = (Date.now() - new Date(iso).getTime()) / 60000;
  return Math.max(0, Math.round(diff));
}
